import { useCallback, useMemo } from 'react';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';

import Flat from '../../../backend/s3/flat';
import Office from '../../../backend/s3/office';
import {
  FLAT_TYPE_GENERIC,
  SEARCH_BY_COUNTY_SUFFIX,
  SEARCH_QUERY_GENERIC,
  SITE_URL,
} from '../../../common/consts';
import {
  BuyHouseOption,
  computeBuyHouseOptions,
} from '../../../common/helpers/buyHouseOption.utils';
import * as flatTypeUtils from '../../../common/helpers/flatType.utils';
import { computeNeighborsIds } from '../../../common/helpers/neighbor.utils';
import {
  SearchOption,
  findSearchOptionByCountyCityAndZone,
} from '../../../common/helpers/searchOption.utils';
import {
  extractCountyCityAndZoneFromQuery,
  extractFlatTypeFromQuery,
  getTitleFromQuery,
  slugifySearchQuery,
} from '../../../common/helpers/searchQuery.utils';
import { computeResults } from '../../../common/helpers/searchService.utils';
import useI18n from '../../../common/hooks/useI18n';
import { IFlat } from '../../../common/model/flat.model';
import { IOffice } from '../../../common/model/office.model';
import { FlatType } from '../../../common/model/enums/flatType.enum';
import { SearchType } from '../../../common/model/enums/searchType.enum';

import { SearchLayout } from '../../../components/search';
import { SHOW_EXTRA_FLATS_COUNT_LIMIT } from '../../../components/search/searchLayout';

type StaticParams = {
  flatType: string;
  query: string;
};

type StaticProps = {
  flatType: string;
  text: string | null;
  offices: IOffice[];
  buyHouseOptions: BuyHouseOption[];
  serializedFlats: string;
  serializedClosedFlats: string;
  serializedResults: string;
  serializedSearchOptions: string;
  neighborsIds: string[];
};

type Props = StaticProps;

const ClosedSearchPage: NextPage<Props> = ({
  flatType,
  text,
  offices,
  buyHouseOptions,
  serializedFlats,
  serializedClosedFlats,
  serializedResults,
  serializedSearchOptions,
  neighborsIds,
}) => {
  const router = useRouter();
  const i18n = useI18n();

  const getCanonicalQuery = useCallback(() => {
    const canonicalQuery = (router.query.query as string).replaceAll('--', '/');
    if (canonicalQuery === SEARCH_QUERY_GENERIC) {
      return '';
    }
    return `/${canonicalQuery}`;
  }, [router.query.query]);

  const htmlTitle = useMemo(
    () =>
      getTitleFromQuery(
        text || 'Inmobiliaria Núcleo',
        SearchType.CLOSED,
        flatType,
        i18n
      ),
    [flatType, i18n, text]
  );
  const canonicalUrl = useMemo(
    () => `${SITE_URL}/comprar/pisos${getCanonicalQuery()}`,
    [getCanonicalQuery]
  );
  const flats: IFlat[] = useMemo(() => JSON.parse(serializedFlats), [
    serializedFlats,
  ]);
  const closedFlats: IFlat[] = useMemo(
    () => JSON.parse(serializedClosedFlats),
    [serializedClosedFlats]
  );
  const results: IFlat[] = useMemo(() => JSON.parse(serializedResults), [
    serializedResults,
  ]);
  const searchOptions: SearchOption[] = useMemo(
    () => JSON.parse(serializedSearchOptions),
    [serializedSearchOptions]
  );

  return (
    <SearchLayout
      canonicalUrl={canonicalUrl}
      htmlTitle={htmlTitle}
      offices={offices}
      buyHouseOptions={buyHouseOptions}
      flats={flats}
      closedFlats={closedFlats}
      results={results}
      searchOptions={searchOptions}
      neighborsIds={neighborsIds}
      metaRobotsNode={
        typeof window !== 'undefined' &&
        canonicalUrl !== window.location.href ? (
          <meta name="robots" content="noindex,nofollow" />
        ) : undefined
      }
      searchBarText={text || undefined}
    />
  );
};

export const getStaticPaths: GetStaticPaths<StaticParams> = async () => {
  const [_, searchOptions] = await Flat.getFlats();
  const queries = [
    SEARCH_QUERY_GENERIC,
    ...searchOptions.map((option) => {
      const countyPart = option.county
        ? slugifySearchQuery(option.county) + SEARCH_BY_COUNTY_SUFFIX
        : undefined;
      const cityPart = option.city
        ? slugifySearchQuery(option.city)
        : undefined;
      const zonePart = option.zone
        ? slugifySearchQuery(option.zone)
        : undefined;
      if (zonePart) {
        return `${countyPart}--${cityPart}--${zonePart}`;
      }
      if (cityPart) {
        return `${countyPart}--${cityPart}`;
      }
      if (countyPart) {
        return countyPart;
      }
      return SEARCH_QUERY_GENERIC;
    }),
  ];

  const allParams = [
    ...queries.map((query) => ({
      flatType: flatTypeUtils.getFlatTypeSlug(FLAT_TYPE_GENERIC, true),
      query,
    })),
    ...queries.map((query) => ({
      flatType: flatTypeUtils.getFlatTypeSlug(FlatType.PISO, true),
      query,
    })),
  ];

  return {
    paths: allParams.map((params) => ({ params })),
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<
  StaticProps,
  StaticParams
> = async ({ params }) => {
  const { flatType: flatTypeFromQuery, query } = params;

  const [flats, searchOptions] = await Flat.getFlats();
  const closedFlats = await Flat.getClosedFlats();

  const [county, city, zone] = extractCountyCityAndZoneFromQuery(query);
  const flatType = extractFlatTypeFromQuery(flatTypeFromQuery);

  if (flatTypeFromQuery !== FLAT_TYPE_GENERIC && !flatType) {
    return {
      notFound: true,
    };
  }

  const searchOption = findSearchOptionByCountyCityAndZone(
    searchOptions,
    county,
    city,
    zone
  );

  if (!searchOption && query !== SEARCH_QUERY_GENERIC) {
    return {
      notFound: true,
    };
  }

  const results = computeResults(flats, {
    county,
    city,
    zone,
    types: flatType ? [flatType] : undefined,
  });

  let neighborsIds: string[] = [];
  if (results.length < SHOW_EXTRA_FLATS_COUNT_LIMIT) {
    neighborsIds = computeNeighborsIds(flats, query);
  }

  const offices = await Office.getOffices();
  const buyHouseOptions = computeBuyHouseOptions(searchOptions);

  return {
    props: {
      flatType,
      text: searchOption ? searchOption.text : null,
      offices,
      buyHouseOptions,
      serializedFlats: JSON.stringify(flats),
      serializedClosedFlats: JSON.stringify(closedFlats),
      serializedResults: JSON.stringify(results),
      serializedSearchOptions: JSON.stringify(searchOptions),
      neighborsIds,
    },
  };
};

export default ClosedSearchPage;
